const KEY = "postshiba-learn"

const GLOW_HEX = ["#3894fa", "#857afa", "#9e47eb", "#2ec794", "#1ab3ad"]

const TAG_FACE = {
  green: "bg-[#243d30] text-[#74d19c]",
  yellow: "bg-[#564328] text-[#ebc56e]",
  red: "bg-[#522e2a] text-[#f59f96]",
  gray: "bg-[#373737] text-[#d4d4d4]"
}

const TAG_BASE = "inline-flex h-5 shrink-0 items-center gap-1 whitespace-nowrap rounded px-1.5 text-[12px] font-medium leading-none ring-1 ring-inset ring-white/[0.06]"

function emptyState() {
  return {completed: {}, checklist: {}, last: null}
}

function read() {
  try {
    const raw = window.localStorage.getItem(KEY)
    if (!raw) return emptyState()
    const data = JSON.parse(raw)
    return {
      completed: data.completed && typeof data.completed === "object" ? data.completed : {},
      checklist: data.checklist && typeof data.checklist === "object" ? data.checklist : {},
      last: typeof data.last === "string" ? data.last : null
    }
  } catch (_error) {
    return emptyState()
  }
}

function write(data) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(data))
  } catch (_error) {
    // Private mode or a full store. The page still works without persistence.
  }
}

export function get() {
  return read()
}

export function markComplete(slug) {
  if (!slug) return read()
  const data = read()
  if (!data.completed[slug]) data.completed[slug] = new Date().toISOString()
  write(data)
  return data
}

export function isComplete(slug) {
  return Boolean(slug && read().completed[slug])
}

export function setLast(slug) {
  if (!slug) return read()
  const data = read()
  data.last = slug
  write(data)
  return data
}

export function setChecklist(id, on) {
  const data = read()
  if (on) data.checklist[id] = true
  else delete data.checklist[id]
  write(data)
  return data.checklist
}

export function getChecklist() {
  return read().checklist
}

export function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

export function el(tag, className, text) {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text != null) node.textContent = text
  return node
}

export function marketingTag(label, tone, dot) {
  const node = el("span", `${TAG_BASE} ${TAG_FACE[tone] || TAG_FACE.gray}`)
  if (dot) node.append(el("span", "size-1.5 rounded-full bg-current"))
  node.append(document.createTextNode(label))
  return node
}

export function copyText(text, statusEl, message) {
  const note = message || "Copied."
  if (!navigator.clipboard) {
    if (statusEl) statusEl.textContent = "Copy is not available."
    return
  }
  navigator.clipboard.writeText(text).then(() => {
    if (!statusEl) return
    statusEl.textContent = note
    window.setTimeout(() => {
      if (statusEl.textContent === note) statusEl.textContent = ""
    }, 1600)
  }).catch(() => {
    if (statusEl) statusEl.textContent = "Copy is not available."
  })
}

export function liveSlugs(root) {
  return String(root.dataset.liveSlugs || "").split(",").map((slug) => slug.trim()).filter(Boolean)
}

function celebrate(root) {
  const banner = root.querySelector("[data-learn-celebrate]")
  if (!banner) return
  banner.hidden = false
  if (banner.dataset.learnConfetti === "true" || prefersReducedMotion()) return
  banner.dataset.learnConfetti = "true"
  if (getComputedStyle(banner).position === "static") banner.style.position = "relative"
  const layer = el("div", "pointer-events-none absolute inset-0 overflow-hidden")
  layer.setAttribute("aria-hidden", "true")
  for (let i = 0; i < 22; i += 1) {
    const piece = el("div", "absolute size-1.5 rounded-sm")
    piece.style.background = GLOW_HEX[i % GLOW_HEX.length]
    piece.style.left = "50%"
    piece.style.top = "30%"
    piece.style.opacity = "1"
    piece.style.transform = "translate(-50%, 0) scale(1)"
    piece.style.transition = "transform 400ms ease-out, opacity 400ms ease-out"
    layer.append(piece)
    window.requestAnimationFrame(() => {
      const x = (Math.random() - 0.5) * 220
      const y = 20 + Math.random() * 120
      piece.style.transitionDuration = `${1200 + Math.random() * 200}ms`
      piece.style.transform = `translate(calc(-50% + ${x}px), ${y}px) scale(0.8)`
      piece.style.opacity = "0"
    })
  }
  banner.append(layer)
  window.setTimeout(() => layer.remove(), 1500)
}

function refresh(root) {
  const slugs = liveSlugs(root)
  const done = slugs.filter(isComplete)
  const fraction = slugs.length ? done.length / slugs.length : 0
  root.querySelectorAll("[data-learn-card][data-learn-slug]").forEach((card) => {
    if (isComplete(card.dataset.learnSlug)) card.dataset.complete = "true"
    else delete card.dataset.complete
  })
  root.querySelectorAll("[data-learn-count]").forEach((node) => {
    node.textContent = `${done.length} of ${slugs.length} lessons done`
  })
  root.querySelectorAll("[data-learn-bar]").forEach((bar) => {
    bar.style.transform = `scaleX(${fraction})`
  })
  const next = slugs.find((slug) => !isComplete(slug))
  root.querySelectorAll("[data-learn-continue]").forEach((link) => {
    if (!next) {
      link.hidden = true
      return
    }
    link.href = `/learn/${next}`
    const label = link.querySelector("[data-learn-continue-label]")
    if (label) label.textContent = done.length ? "Continue" : "Start the course"
    link.hidden = false
  })
  if (slugs.length && done.length === slugs.length) celebrate(root)
}

export function mountProgress(scope) {
  const rootDoc = scope || document
  const roots = []
  if (rootDoc.matches?.("[data-learn-root]")) roots.push(rootDoc)
  rootDoc.querySelectorAll("[data-learn-root]").forEach((node) => roots.push(node))
  roots.forEach((root) => {
    if (root.dataset.learnProgressBound === "true") {
      refresh(root)
      return
    }
    root.dataset.learnProgressBound = "true"
    if (root.dataset.lessonSlug) setLast(root.dataset.lessonSlug)
    refresh(root)
  })
}

if (!window.__learnProgressEvents) {
  window.__learnProgressEvents = true
  document.addEventListener("learn:complete", () => {
    document.querySelectorAll("[data-learn-root]").forEach((root) => refresh(root))
  })
}
